import { useCallback, useEffect, useMemo, useRef } from 'react';

import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router';

import { SectionLayout } from '@/components/atoms/SectionLayout/SectionLayout';
import { SettingsContent } from '@/components/templates/SettingsContent/SettingsContent';
import { usePrefetchRolePermissions } from '@/hooks/useRolePermissions';
import { useRoles } from '@/hooks/useRoles';
import { useSidebar } from '@/hooks/useSidebar';
import { getPermissions } from '@/redux/permissions/selectors';
import { selectAccountSlug } from '@/redux/session/selectors';
import useSegments from '@/segment/useSegments';
import { actions } from '@/shared/types/permissions';
import { PageName } from '@/shared/types/segment';
import { RootState } from '@/shared/types/state';
import { extractNames } from '@/util/strings';
import { SYSTEM_ROLE_COPY, SYSTEM_ROLE_ORDER } from '@/util/systemRoles';

import { CustomRoleCard } from './CustomRoleCard';
import { copyRoleName, newRolePath, NewRoleState } from './newRole';
import { DuplicateAction } from './RoleActionsMenu';
import { RoleCard, RoleCardSkeleton } from './RoleCard';
import { useDeleteRoleModal } from './useDeleteRoleModal';

export const ManageAccessRoles = () => {
  useSegments(PageName.MANAGE_ROLE_ACCESS);

  const { t } = useTranslation();
  const navigate = useNavigate();
  const slug = useSelector(selectAccountSlug);
  const { roles, listStatus, updateRole, updateStatus, updateVariables } = useRoles();
  const confirmDelete = useDeleteRoleModal();
  const prefetchRolePermissions = usePrefetchRolePermissions();
  const { open, reset, isOpen } = useSidebar();
  const isPanelOpen = isOpen('role_permissions');

  const canWrite = useSelector((state: RootState) => getPermissions(state, 'roles', actions.WRITE));
  const canDelete = useSelector((state: RootState) =>
    getPermissions(state, 'roles', actions.DELETE)
  );

  const systemRoles = useMemo(
    () =>
      roles
        .filter((role) => !role.account_id && SYSTEM_ROLE_ORDER.includes(role.role_id))
        .sort((a, b) => SYSTEM_ROLE_ORDER.indexOf(a.role_id) - SYSTEM_ROLE_ORDER.indexOf(b.role_id))
        .map((role) => ({
          role_id: role.role_id,
          name: t(SYSTEM_ROLE_COPY[role.role_id].name, role.name),
          description: t(SYSTEM_ROLE_COPY[role.role_id].description, role.description),
        })),
    [roles, t]
  );

  const customRoles = useMemo(
    () => roles.filter((role) => !!role.account_id).sort((a, b) => a.name.localeCompare(b.name)),
    [roles]
  );

  const copyNames = useMemo(() => {
    const taken = extractNames(roles);
    return new Map(customRoles.map((role) => [role.role_id, copyRoleName(role.name, taken)]));
  }, [customRoles, roles]);

  // Only tear down a panel this tab opened itself, never one another surface pinned.
  const isPanelOpenRef = useRef(isPanelOpen);
  isPanelOpenRef.current = isPanelOpen;

  useEffect(
    () => () => {
      if (isPanelOpenRef.current) {
        reset();
      }
    },
    [reset]
  );

  const openRoleEditor = useCallback(
    (roleId: string) => navigate(`/${slug}/account/manage/roles/${roleId}`),
    [navigate, slug]
  );

  const duplicateAction = useCallback(
    (roleId: string): DuplicateAction => ({
      name: copyNames.get(roleId),
      onOpen: () => prefetchRolePermissions(roleId),
      onSubmit: (roleName) =>
        navigate(newRolePath(slug), {
          state: { roleName, sourceRoleId: roleId } satisfies NewRoleState,
        }),
    }),
    [copyNames, navigate, prefetchRolePermissions, slug]
  );

  return (
    <SettingsContent className="pt-8 pb-24" centerInParent={isPanelOpen}>
      <div className="flex flex-col gap-6">
        <SectionLayout title={t('roles.system_roles')}>
          {listStatus === 'pending'
            ? SYSTEM_ROLE_ORDER.map((roleId) => <RoleCardSkeleton key={roleId} />)
            : systemRoles.map((role) => (
                <RoleCard
                  key={role.role_id}
                  name={role.name}
                  description={role.description}
                  onViewPermissions={() =>
                    open('role_permissions', { roleId: role.role_id, roleName: role.name })
                  }
                />
              ))}
        </SectionLayout>

        {customRoles.length > 0 && (
          <SectionLayout title={t('roles.custom_roles')}>
            {customRoles.map((role) => (
              <CustomRoleCard
                key={role.role_id}
                name={role.name}
                canWrite={canWrite}
                canDelete={canDelete}
                isRenaming={updateStatus === 'pending' && updateVariables?.role_id === role.role_id}
                duplicate={duplicateAction(role.role_id)}
                onRename={(name, opts) => updateRole({ role_id: role.role_id, name }, opts)}
                onEdit={() => openRoleEditor(role.role_id)}
                onDelete={() => confirmDelete(role)}
              />
            ))}
          </SectionLayout>
        )}
      </div>
    </SettingsContent>
  );
};
